import { useEffect, useState, useCallback } from 'react'
import { Key, Eye, EyeOff, Plus, X, ChevronDown, ChevronRight, GripVertical } from 'lucide-react'
import toast from 'react-hot-toast'
import client from '../api/client'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface ProxyProvider {
  provider: string
  api_key: string | null
  base_url: string | null
  rate_limit: number
  is_active: boolean
  auth_header_type: string
}

interface ProviderModel {
  id: number
  provider: string
  model_name: string
  category: string
}

interface ProviderModelsResponse {
  models: ProviderModel[]
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  openrouter: 'OpenRouter',
  midjourney: 'Midjourney',
  stability: 'Stability AI',
  flux: 'Flux',
  replicate: 'Replicate',
  sora: 'Sora',
  kling: 'Kling',
  veo: 'Veo',
  suno: 'Suno',
  serper: 'Serper',
  brave: 'Brave Search',
  together: 'Together.ai',
  fal: 'Fal.ai',
  typegpt: 'GPT-Chatbot',
}

const PROVIDER_ICONS: Record<string, string> = {
  openai: '🟢',
  anthropic: '🟠',
  openrouter: '🟣',
  midjourney: '🎨',
  stability: '🖌️',
  flux: '⚡',
  replicate: '🔄',
  together: '🤖',
  fal: '🦅',
  typegpt: '🤖',
  sora: '🎬',
  kling: '🐉',
  veo: '🎥',
  suno: '🎵',
  serper: '🔍',
  brave: '🦁',
}

const CATEGORY_LABELS: Record<string, string> = {
  chat: 'Чат',
  image: 'Изображения',
  video: 'Видео',
  music: 'Музыка',
}

const ORDER_KEY = 'neuralspace_provider_order'

// ────────────────────────────────────────────────────────────
// Sortable Provider Card
// ────────────────────────────────────────────────────────────

function SortableProviderCard({
  provider,
  form,
  isVisible,
  isExpanded,
  providerModels,
  newInput,
  onToggleVisibility,
  onToggleExpanded,
  onToggleActive,
  onSave,
  onTest,
  onEditFormChange,
  onAddModel,
  onDeleteModel,
  onNewModelNameChange,
  onNewModelCategoryChange,
}: {
  provider: ProxyProvider
  form: { api_key: string; base_url: string; rate_limit: number; auth_header_type: string }
  isVisible: boolean
  isExpanded: boolean
  providerModels: ProviderModel[]
  newInput: { model_name: string; category: string }
  onToggleVisibility: () => void
  onToggleExpanded: () => void
  onToggleActive: () => void
  onSave: () => void
  onTest: () => void
  onEditFormChange: (field: string, value: string | number) => void
  onAddModel: () => void
  onDeleteModel: (id: number) => void
  onNewModelNameChange: (value: string) => void
  onNewModelCategoryChange: (value: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: provider.provider })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`glass-card p-4 transition-all ${provider.is_active ? 'border-indigo-500/20' : 'opacity-60'} ${isDragging ? 'shadow-xl shadow-indigo-500/10' : ''}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Drag handle */}
          <button
            className="cursor-grab active:cursor-grabbing p-0.5 text-gray-500 hover:text-gray-300 transition-colors touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-lg">{PROVIDER_ICONS[provider.provider]}</span>
          <span className="text-sm font-medium text-gray-200">{PROVIDER_LABELS[provider.provider]}</span>
          <span className="text-[10px] text-gray-600">{provider.provider}</span>
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle */}
          <button
            onClick={onToggleActive}
            className={`relative w-9 h-5 rounded-full transition-all ${
              provider.is_active ? 'bg-indigo-500' : 'bg-gray-700'
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                provider.is_active ? 'left-[18px]' : 'left-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {/* API Key */}
      <div className="space-y-2">
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">API Key</label>
          <div className="flex gap-1">
            <input
              type={isVisible ? 'text' : 'password'}
              value={form.api_key}
              onChange={(e) => onEditFormChange('api_key', e.target.value)}
              placeholder="sk-..."
              className="input-field flex-1 text-xs py-1.5 px-2"
            />
            <button
              onClick={onToggleVisibility}
              className="p-1.5 rounded-lg glass text-gray-500 hover:text-gray-300"
            >
              {isVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {/* Base URL */}
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">Base URL</label>
          <input
            type="text"
            value={form.base_url}
            onChange={(e) => onEditFormChange('base_url', e.target.value)}
            placeholder="https://api.openai.com/v1"
            className="input-field w-full text-xs py-1.5 px-2"
          />
        </div>

        {/* Rate Limit */}
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-gray-500">Rate Limit:</label>
          <input
            type="number"
            value={form.rate_limit}
            onChange={(e) => onEditFormChange('rate_limit', parseInt(e.target.value) || 60)}
            min={1}
            max={1000}
            className="input-field w-20 text-xs py-1 px-2 text-center"
          />
          <span className="text-[10px] text-gray-600">запросов/мин</span>
        </div>

        {/* Auth Header Type */}
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">Authentication Header</label>
          <select
            value={form.auth_header_type || 'Bearer'}
            onChange={(e) => onEditFormChange('auth_header_type', e.target.value)}
            className="input-field w-full text-xs py-1.5 px-2"
          >
            <option value="Bearer">Bearer — Authorization: Bearer {`{key}`}</option>
            <option value="Key">Key — Authorization: Key {`{key}`}</option>
            <option value="Basic">Basic — Authorization: Basic {`{key}`}</option>
          </select>
        </div>

        {/* Save + Test buttons */}
        <div className="flex gap-2 mt-1">
          <button
            onClick={onSave}
            className="flex-1 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs hover:bg-indigo-500/20 transition-all"
          >
            Сохранить
          </button>
          <button
            onClick={onTest}
            className="py-1.5 px-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs hover:bg-emerald-500/20 transition-all"
          >
            Проверить
          </button>
        </div>
      </div>

      {/* Model Management Block */}
      <div className="mt-3 pt-3 border-t border-gray-700/50">
        <button
          onClick={onToggleExpanded}
          className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-300 transition-colors w-full"
        >
          {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          <span>Модели ({providerModels.length})</span>
        </button>

        {isExpanded && (
          <div className="mt-2 space-y-2">
            {/* Model list */}
            {providerModels.length === 0 && (
              <p className="text-[10px] text-gray-600 italic">Нет добавленных моделей</p>
            )}
            {providerModels.map((model) => (
              <div
                key={model.id}
                className="flex items-center justify-between bg-gray-800/50 rounded px-2 py-1"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs text-gray-200 truncate">{model.model_name}</span>
                  <span className="text-[9px] text-gray-500 shrink-0 bg-gray-700/50 px-1 rounded">
                    {CATEGORY_LABELS[model.category] || model.category}
                  </span>
                </div>
                <button
                  onClick={() => onDeleteModel(model.id)}
                  className="p-0.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}

            {/* Add model row */}
            <div className="flex items-center gap-1.5 mt-2">
              <input
                type="text"
                value={newInput.model_name}
                onChange={(e) => onNewModelNameChange(e.target.value)}
                placeholder="gpt-5, claude-4, ..."
                className="input-field flex-1 text-xs py-1 px-2 min-w-0"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onAddModel()
                }}
              />
              <select
                value={newInput.category}
                onChange={(e) => onNewModelCategoryChange(e.target.value)}
                className="input-field text-xs py-1 px-1 w-24"
              >
                <option value="chat">Чат</option>
                <option value="image">Изобр.</option>
                <option value="video">Видео</option>
                <option value="music">Музыка</option>
              </select>
              <button
                onClick={onAddModel}
                className="p-1 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 hover:bg-indigo-500/20 transition-all shrink-0"
                title="Добавить модель"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


// ────────────────────────────────────────────────────────────
// ApiKeys — main component
// ────────────────────────────────────────────────────────────

export default function ApiKeys() {
  const [providers, setProviders] = useState<ProxyProvider[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())
  const [editForm, setEditForm] = useState<Record<string, { api_key: string; base_url: string; rate_limit: number; auth_header_type: string }>>({})

  // Model management state
  const [allModels, setAllModels] = useState<ProviderModel[]>([])
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set())
  const [newModelInputs, setNewModelInputs] = useState<Record<string, { model_name: string; category: string }>>({})

  // Сортируем провайдеры согласно сохранённому порядку
  const sortProviders = useCallback((data: ProxyProvider[]) => {
    try {
      const order = JSON.parse(localStorage.getItem(ORDER_KEY) || '[]') as string[]
      const ordered = [...data].sort((a, b) => {
        const ia = order.indexOf(a.provider)
        const ib = order.indexOf(b.provider)
        if (ia === -1 && ib === -1) return 0
        if (ia === -1) return 1
        if (ib === -1) return -1
        return ia - ib
      })
      return ordered
    } catch {
      return data
    }
  }, [])

  useEffect(() => {
    fetchProviders()
    fetchModels()
  }, [])

  const fetchProviders = async () => {
    try {
      const { data } = await client.get('/admin/proxy-config')
      const sorted = sortProviders(data)
      setProviders(sorted)
      // Инициализируем формы
      const forms: Record<string, any> = {}
      sorted.forEach((p: ProxyProvider) => {
        forms[p.provider] = {
          api_key: p.api_key || '',
          base_url: p.base_url || '',
          rate_limit: p.rate_limit || 60,
          auth_header_type: p.auth_header_type || 'Bearer',
        }
      })
      setEditForm(forms)
    } catch {
      toast.error('Ошибка загрузки конфигурации')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchModels = async () => {
    try {
      const { data } = await client.get<ProviderModelsResponse>('/admin/provider-models')
      setAllModels(data.models || [])
    } catch {
      // ignore
    }
  }

  const toggleVisibility = (provider: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev)
      if (next.has(provider)) next.delete(provider)
      else next.add(provider)
      return next
    })
  }

  const toggleExpanded = (provider: string) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev)
      if (next.has(provider)) next.delete(provider)
      else next.add(provider)
      return next
    })
  }

  const toggleActive = async (provider: string, currentActive: boolean) => {
    const form = editForm[provider]
    if (!form) return

    try {
      await client.post('/admin/proxy-config', {
        provider,
        api_key: form.api_key || null,
        base_url: form.base_url || null,
        rate_limit: form.rate_limit,
        is_active: !currentActive,
        auth_header_type: form.auth_header_type || 'Bearer',
      })
      setProviders((prev) =>
        prev.map((p) => (p.provider === provider ? { ...p, is_active: !currentActive } : p))
      )
      toast.success(`${PROVIDER_LABELS[provider] || provider} ${!currentActive ? 'включён' : 'отключён'}`)
    } catch {
      toast.error('Ошибка при обновлении')
    }
  }

  const saveProvider = async (provider: string) => {
    const form = editForm[provider]
    if (!form) return

    try {
      await client.post('/admin/proxy-config', {
        provider,
        api_key: form.api_key || null,
        base_url: form.base_url || null,
        rate_limit: form.rate_limit,
        is_active: true,
        auth_header_type: form.auth_header_type || 'Bearer',
      })
      setProviders((prev) =>
        prev.map((p) =>
          p.provider === provider
            ? { ...p, api_key: form.api_key, base_url: form.base_url, rate_limit: form.rate_limit, is_active: true, auth_header_type: form.auth_header_type || 'Bearer' }
            : p
        )
      )
      toast.success(`${PROVIDER_LABELS[provider] || provider} сохранён`)
    } catch {
      toast.error('Ошибка при сохранении')
    }
  }

  const testProvider = async (provider: string) => {
    try {
      const { data } = await client.post('/admin/proxy-config/test', { provider })
      if (data.status === 'ok') {
        toast.success(`${PROVIDER_LABELS[provider] || provider}: ключ работает`)
      } else {
        toast.error(`${PROVIDER_LABELS[provider] || provider}: ${data.message}`)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка проверки ключа')
    }
  }

  const getProviderModels = (provider: string): ProviderModel[] => {
    return allModels.filter((m) => m.provider === provider)
  }

  const deleteModel = async (modelId: number) => {
    try {
      await client.delete(`/admin/provider-models/${modelId}`)
      setAllModels((prev) => prev.filter((m) => m.id !== modelId))
      toast.success('Модель удалена')
    } catch {
      toast.error('Ошибка при удалении модели')
    }
  }

  const addModel = async (provider: string) => {
    const input = newModelInputs[provider]
    if (!input || !input.model_name.trim()) {
      toast.error('Введите имя модели')
      return
    }

    try {
      const { data } = await client.post('/admin/provider-models', {
        provider,
        model_name: input.model_name.trim(),
        category: input.category || 'chat',
      })
      setAllModels((prev) => [...prev, data])
      setNewModelInputs((prev) => ({
        ...prev,
        [provider]: { model_name: '', category: prev[provider]?.category || 'chat' },
      }))
      toast.success('Модель добавлена')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка при добавлении модели')
    }
  }

  // Drag-end handler
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setProviders((prev) => {
      const oldIndex = prev.findIndex((p) => p.provider === active.id)
      const newIndex = prev.findIndex((p) => p.provider === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      const reordered = arrayMove(prev, oldIndex, newIndex)
      // Сохраняем порядок в localStorage
      const order = reordered.map((p) => p.provider)
      localStorage.setItem(ORDER_KEY, JSON.stringify(order))
      return reordered
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-200 flex items-center gap-2">
        <Key className="w-5 h-5 text-indigo-400" />
        API-ключи провайдеров
      </h2>
      <p className="text-xs text-gray-500">
        Управление API-ключами и моделями для AI-провайдеров. Ключи хранятся в зашифрованном виде.
        Карточки можно перетаскивать для изменения порядка.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={providers.map((p) => p.provider)}
          strategy={verticalListSortingStrategy}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map((provider) => {
              const form = editForm[provider.provider] || { api_key: '', base_url: '', rate_limit: 60, auth_header_type: 'Bearer' }
              const isVisible = visibleKeys.has(provider.provider)
              const isExpanded = expandedProviders.has(provider.provider)
              const pModels = getProviderModels(provider.provider)
              const newInput = newModelInputs[provider.provider] || { model_name: '', category: 'chat' }

              return (
                <SortableProviderCard
                  key={provider.provider}
                  provider={provider}
                  form={form}
                  isVisible={isVisible}
                  isExpanded={isExpanded}
                  providerModels={pModels}
                  newInput={newInput}
                  onToggleVisibility={() => toggleVisibility(provider.provider)}
                  onToggleExpanded={() => toggleExpanded(provider.provider)}
                  onToggleActive={() => toggleActive(provider.provider, provider.is_active)}
                  onSave={() => saveProvider(provider.provider)}
                  onTest={() => testProvider(provider.provider)}
                  onEditFormChange={(field, value) =>
                    setEditForm((prev) => ({
                      ...prev,
                      [provider.provider]: { ...prev[provider.provider], [field]: value },
                    }))
                  }
                  onAddModel={() => addModel(provider.provider)}
                  onDeleteModel={(id) => deleteModel(id)}
                  onNewModelNameChange={(value) =>
                    setNewModelInputs((prev) => ({
                      ...prev,
                      [provider.provider]: { ...prev[provider.provider], model_name: value },
                    }))
                  }
                  onNewModelCategoryChange={(value) =>
                    setNewModelInputs((prev) => ({
                      ...prev,
                      [provider.provider]: { ...prev[provider.provider], category: value },
                    }))
                  }
                />
              )
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
